import { Capacitor } from '@capacitor/core';
import { NotificationService } from '@/services/notification.service';

// Piso de 5 min no app: o GET /notifications não pagina e cruza a base inteira do
// aluno por e-mail (els.EAD_Notificacoes_BuscarPorEmail). Com o loop consertado,
// 60 s viraria carga nova e constante no SQL — e no app o sino já é recarregado
// pelo push em primeiro plano, pelo resume e por toda navegação.
const MIN_APP_REFRESH_MS = 5 * 60 * 1000;
const REFRESH_MS = Math.max(+process.env.VUE_APP_NOTIFICATION_REFRESH_TIME || 60000, MIN_APP_REFRESH_MS);

// Geração do loop. O clearLoop a incrementa: um tick que já estava buscando
// quando o loop parou (logout) não pode reagendar ao voltar — se um login novo
// tiver ligado outro loop nesse meio-tempo, seriam duas cadeias e o clearLoop só
// pararia a última. Fica fora do state: não é dado de tela.
let _loopGeneration = 0;

export default {
  namespaced: true,
  state: {
    notifications: [],
    // Id do setTimeout do polling do sino. 0 = parado.
    intervalId: 0,
  },
  getters: {
    unread: state => (state.notifications || []).filter(n => n && n.readOn == null),
  },
  actions: {
    // `silent`: o polling não toasta erro — um 500 do backend viraria um toast a
    // cada minuto. As chamadas disparadas por navegação mantêm o toast.
    getNotifications({ commit }, { silent = false } = {}) {
      return NotificationService.getNotifications()
        .then(response => {
          commit('SET_NOTIFICATIONS', response.data);
        })
        .catch(error => {
          if (!silent) commit('toast/NEW', { type: 'error', message: error.message, suppressOffline: true }, { root: true });
        })
    },

    // Polling do sino. Antes era um setTimeout ÚNICO (rodava uma vez, 60 s depois
    // de quem chamasse) e o clearLoop fazia clearInterval de um id que nunca era
    // gravado — ou seja, não havia polling e não havia o que parar. Agora é um
    // setTimeout encadeado (nunca sobrepõe duas buscas lentas), idempotente
    // (chamar de novo com o loop vivo não cria outro) e parável pelo clearLoop.
    //
    // Só no app nativo. Na web o comportamento continua o de antes (busca a cada
    // navegação, pelo checkAccessMiddleware): ligar polling para todo aluno com o
    // site aberto seria carga nova no SQL sem ganho que justifique.
    initLoopTimeout({ commit, state, dispatch, rootState }) {
      if (!Capacitor.isNativePlatform()) return;
      if (state.intervalId) return;
      const generation = ++_loopGeneration;
      const tick = () => {
        if (generation !== _loopGeneration) return;
        // Sessão acabou entre um tick e outro: para sozinho.
        if (!sessionStorage.getItem('accessToken')) {
          commit('SET_INTERVALID', 0);
          return;
        }
        const run = (!rootState.dom.isConnected || document.hidden)
          ? Promise.resolve()
          : dispatch('getNotifications', { silent: true });
        run.finally(() => {
          // clearLoop chamado durante a busca (mesmo que outro loop já tenha
          // começado): este não reagenda.
          if (generation !== _loopGeneration || !state.intervalId) return;
          commit('SET_INTERVALID', setTimeout(tick, REFRESH_MS));
        });
      };
      commit('SET_INTERVALID', setTimeout(tick, REFRESH_MS));
    },

    clearLoop({ commit, state }) {
      _loopGeneration++;
      if (state.intervalId) clearTimeout(state.intervalId);
      commit('SET_INTERVALID', 0);
    },

    // Otimista: a notificação sai das não lidas no toque — o "x" do sino não pode
    // ficar esperando a rede para responder. Se o PUT falhar, volta ao que era.
    read({ commit, state }, notificationId) {
      const current = (state.notifications || []).find(n => n && n.id == notificationId);
      const previous = current ? current.readOn : null;
      commit('MARK_READ', notificationId);

      return NotificationService.read(notificationId)
        .catch(error => {
          commit('SET_READ_ON', { id: notificationId, readOn: previous });
          commit('toast/NEW', { type: 'error', message: error.message, suppressOffline: true }, { root: true });
        });
    },
  },
  mutations: {
    SET_NOTIFICATIONS(state, data) {
      state.notifications = data
    },

    SET_INTERVALID(state, intervarId) {
      state.intervalId = intervarId
    },

    MARK_READ(state, notificationId) {
      for (var i in state.notifications) {
        if (state.notifications[i].id == notificationId) {
          state.notifications[i].readOn = (new Date()).toString()
        }
      }
    },

    // Desfaz o MARK_READ otimista quando o PUT falha.
    SET_READ_ON(state, { id, readOn }) {
      const notification = (state.notifications || []).find(n => n && n.id == id);
      if (notification) notification.readOn = readOn;
    }
  }
}
